import Accordion from "@/common/components/Accordion";
import Button from "@/common/components/Button";
import { ImageCarousel } from "@/common/components/ImageCarousel";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import MaintenanceObjective from "@/common/components/MaintenanceObjective";
import ResumeItem from "@/common/components/ResumeItem";
import ReviewDescription from "@/common/components/ReviewDescription";
import { cn } from "@/common/utils/cn";
import { formatCurrency } from "@/common/utils/formatters";
import { MaintenanceDirectiveObjectiveData } from "@/features/maintenance/features/workflows/types/forwardings/directiveEntries/directiveEntry.type";
import { CameraIcon } from "lucide-react";
import ExecutionEntryObjectiveActivities from "../ExecutionEntryDiagnostic/ExecutionEntryObjectiveActivities";
import ExecutionEntryObjectivePieces from "../ExecutionEntryDiagnostic/ExecutionEntryObjectivePieces";
import ExecutionObjectiveComplete from "./ExecutionObjectiveComplete";

interface ExecutionObjectiveItemProps {
  objective: MaintenanceDirectiveObjectiveData;
  isInProgress: boolean;
}

const ExecutionObjectiveItem = ({
  objective,
  isInProgress,
}: ExecutionObjectiveItemProps) => {
  const isDisapproved = objective.last_review?.state === 'disapproved';

  const piecesCount = objective.execution_pieces?.length || 0;
  const activitiesCount = objective.execution_activities?.length || 0;

  const piecesTotalValue =
    objective.execution_pieces?.reduce((prev, curr) => prev + Number(curr.total_value), 0) || 0;
  const activitiesTotalValue =
    objective.execution_activities?.reduce((prev, curr) => prev + Number(curr.total_value), 0) || 0;

  const piecesCountDisplay =
    piecesCount === 0 ? 'Nenhuma peça' : piecesCount === 1 ? '1 Peça' : `${piecesCount} Peças`;
  const activitiesCountDisplay =
    activitiesCount === 0 ? 'Nenhum serviço' : activitiesCount === 1 ? '1 Serviço' : `${activitiesCount} Serviços`;

  return (
    <Accordion
      key={objective.id}
      className={cn(
        'p-0 rounded-lg',
        objective.status === 'complete' && isInProgress && !isDisapproved && 'bg-success-op',
        isDisapproved && 'bg-yellow-op-2',
      )}
      triggerClassName="p-0 pr-4 bg-white-100 rounded-t-lg"
      bodyClassName="p-4"
      head={(
        <div className="flex items-center flex-wrap gap-4 w-full pr-4">
          <div className="flex items-start flex-wrap gap-4">
            <div onClick={(e) => e.stopPropagation()}>
              <ImageCarousel
                files={objective.photos?.map((photo) => ({
                  id: photo.id,
                  file: photo.url,
                }))}
                Placeholder={(
                  <ImageWithPlaceholder
                    className={cn(
                      'size-28 rounded-md',
                      isDisapproved && 'size-40',
                    )}
                    iconClassName="size-14"
                    placeholderIcon={CameraIcon}
                  />
                )}
                className={cn(
                  'size-28',
                  isDisapproved && 'size-40',
                )}
                showExpandButton
                showRemoveButton={false}
              />
            </div>

            <div className="flex flex-col items-start gap-2 py-3 min-w-0">
              <MaintenanceObjective
                systemDisplayName={objective.system.display_name}
                actionDisplayName={objective.action.display_name}
                partDisplayName={objective.part.display_name}
                actionDescription={objective.action.description}
              />

              {isDisapproved && (
                <ResumeItem
                  containerClassName="items-start"
                  className="text-xs"
                  label="Justificativa"
                  value={objective?.last_review?.description || '--'}
                />
              )}
            </div>
          </div>

          <div className="ml-auto flex items-center gap-3">
            <div className="flex shrink-0 flex-col items-end gap-0.5 text-xs">
              <div className="flex items-center gap-0.5">
                <span>{piecesCountDisplay}</span>

                <span>•</span>

                <span className="text-valid">{formatCurrency(piecesTotalValue)}</span>
              </div>

              <div className="flex items-center gap-0.5">
                <span>{activitiesCountDisplay}</span>

                <span>•</span>

                <span className="text-valid">{formatCurrency(activitiesTotalValue)}</span>
              </div>
            </div>

            {isInProgress && (
              <div onClick={(e) => e.stopPropagation()}>
                {(objective.status !== 'complete' || isDisapproved) && (
                  <ExecutionObjectiveComplete
                    objective={objective}
                  />
                )}

                {objective.status === 'complete' && !isDisapproved && (
                  <Button variant="neutral">
                    Finalizado
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      body={(
        <div className="flex flex-col gap-3">
          {isDisapproved && isInProgress && (
            <ReviewDescription
              description={objective.last_review?.description}
            />
          )}

          <ExecutionEntryObjectivePieces
            objective={objective}
          />

          <ExecutionEntryObjectiveActivities
            objective={objective}
          />
        </div>
      )}
    />
  );
}

export default ExecutionObjectiveItem;
